import { takeEvery, put, select, call } from 'redux-saga/effects'
import { defaultEditor } from '@aytm/app/insights/dashboard/lib/config'
import {
  getSubjectData,
  getSubjectProps,
  updateEditor,
  updateEditorError
} from '@aytm/app/insights/dashboard/helpers/editor'
import { getCurrentBlock } from '@aytm/app/insights/dashboard/helpers/requests'

import { DataDuck, DATA_ACTIONS } from '../DataDuck'
import { EventDuck, EVENT_ACTIONS } from '../EventDuck'

import { TypedSagaDuck } from './EditorDuck.types'
import { EDITOR_ACTIONS } from './EditorDuck.actions'
import { imageUploadSaga } from './EditorDuck.sagas'

import { duckName } from './constants'
import { safelyExecuteSaga } from '@aytm/app/insights/dashboard/lib/safety'

const EditorDuck = TypedSagaDuck(duckName, 'insights-dashboard')

// actions

EditorDuck.action(EDITOR_ACTIONS.RESET)
EditorDuck.action(EDITOR_ACTIONS.UPDATE)
EditorDuck.action(EDITOR_ACTIONS.SET_EDIT)
EditorDuck.action(EDITOR_ACTIONS.SET_RENAME)
EditorDuck.action(EDITOR_ACTIONS.SET_DELETE)
EditorDuck.action(EDITOR_ACTIONS.SET_REPLACE)
EditorDuck.action(EDITOR_ACTIONS.CHANGE)
EditorDuck.action(EDITOR_ACTIONS.IMAGE_UPLOAD)
EditorDuck.action(EDITOR_ACTIONS.IMAGE_UPLOAD_SUCCESS)
EditorDuck.action(EDITOR_ACTIONS.IMAGE_UPLOAD_FAIL)
EditorDuck.action(EDITOR_ACTIONS.ERROR)

// selectors

EditorDuck.selector('state', state => state)
EditorDuck.selector('editor', state => state.editor)
EditorDuck.selector('items', state => {
  const block = getCurrentBlock(state)
  return block?.getItemsArray() || []
})

// reducers

EditorDuck.reducer(EDITOR_ACTIONS.RESET, state => {
  return {
    ...state,
    editor: defaultEditor
  }
})

EditorDuck.reducer(EDITOR_ACTIONS.SET_EDIT, (state, action) => {
  const { item, itemIndex } = action.payload || {}
  const subjectData = getSubjectData(state, item)
  return updateEditor(state, { type: 'edit', itemIndex, item, data: subjectData, menuIndex: -1 })
})

EditorDuck.reducer(EDITOR_ACTIONS.SET_RENAME, (state, action) => {
  const { item, itemIndex } = action.payload || {}
  const subjectData = getSubjectProps(state, item)
  return updateEditor(state, { type: 'rename', itemIndex, item, data: subjectData, menuIndex: -1 })
})

EditorDuck.reducer(EDITOR_ACTIONS.SET_DELETE, (state, action) => {
  const { item, itemIndex } = action.payload || {}
  const subjectData = getSubjectProps(state, item)
  return updateEditor(state, { type: 'delete', itemIndex, item, data: subjectData, menuIndex: -1 })
})

EditorDuck.reducer(EDITOR_ACTIONS.SET_REPLACE, (state, action) => {
  const { item, itemIndex } = action.payload || {}
  return updateEditor(state, { type: 'replace', itemIndex, item, menuIndex: -1 })
})

EditorDuck.reducer(EDITOR_ACTIONS.CHANGE, (state, action) => {
  const editor = state.editor || {}
  const payload = action.payload || {}
  return updateEditor(state, { ...payload, changed: 'edit' === editor.type })
})

EditorDuck.reducer(EDITOR_ACTIONS.IMAGE_UPLOAD, state => {
  let resultState = updateEditorError(state, { type: 'image' })
  resultState = updateEditor(resultState, { state: 'uploading' })
  return resultState
})

EditorDuck.reducer(EDITOR_ACTIONS.IMAGE_UPLOAD_SUCCESS, (state, action) => {
  const { image } = action.payload || {}
  const editor = state.editor || {}
  const newData = Object.assign({}, { ...editor.data, image })
  let resultState = updateEditorError(state, { type: 'image' })
  resultState = updateEditor(resultState, { data: newData, state: null, changed: true })
  return resultState
})

EditorDuck.reducer(EDITOR_ACTIONS.IMAGE_UPLOAD_FAIL, (state, action) => {
  const { error } = action.payload || {}
  return updateEditorError(state, { type: 'image', message: error || 'image upload failed', state: null })
})

EditorDuck.reducer(EDITOR_ACTIONS.ERROR, (state, action) => {
  const { type, message } = action.payload || {}
  return updateEditorError(state, { type, message })
})

// sagas

EditorDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[EDITOR_ACTIONS.UPDATE].actionType], function* runEvery(action) {
    if (!action.payload || !Object.keys(action.payload).length) {
      // Reset editor
      yield put(duckFace.action[EDITOR_ACTIONS.RESET]())
      return
    }
    const { error, ...restPayload } = action.payload || {}
    if (error && error.type) {
      // Set editor error
      yield put(duckFace.action[EDITOR_ACTIONS.ERROR](error))
    }
    if (!restPayload || !Object.keys(restPayload).length) {
      // Stop processing
      return
    }
    const { type, action: editorAction } = restPayload || {}
    const editor = yield select(duckFace.select.editor)
    const editorType = type || editor?.type
    if (['edit', 'change'].includes(editorType)) {
      if ('change' === editorAction) {
        // Change data in active editor
        const { data } = action.payload || {}
        yield put(duckFace.action[EDITOR_ACTIONS.CHANGE]({ data }))
      } else if ('save' === editorAction) {
        // Save changes in item
        yield put(DataDuck.action[DATA_ACTIONS.SUBJECT_UPDATE]({}))
      } else {
        // Open data editor for item
        yield put(duckFace.action[EDITOR_ACTIONS.SET_EDIT](action.payload))
      }
    } else if ('rename' === editorType) {
      if ('change' === editorAction) {
        yield put(duckFace.action[EDITOR_ACTIONS.CHANGE](action.payload))
      } else if ('save' === editorAction) {
        // Save changes in item
        yield put(DataDuck.action[DATA_ACTIONS.SUBJECT_UPDATE]({}))
      } else {
        // Open rename editor for item
        yield put(duckFace.action[EDITOR_ACTIONS.SET_RENAME](action.payload))
      }
    } else if ('delete' === editorType) {
      // eslint-disable-next-line unicorn/prefer-ternary
      if ('save' === editorAction) {
        // Save changes in item
        yield put(DataDuck.action[DATA_ACTIONS.SUBJECT_UPDATE]({}))
      } else {
        // Open delete editor for item
        yield put(duckFace.action[EDITOR_ACTIONS.SET_DELETE](action.payload))
      }
    } else if ('replace' === editorType) {
      if ('apply' === editorAction) {
        const { data } = action.payload || {}
        const items = yield select(duckFace.select.items)
        const index = items.findIndex(v => data?.uid === v.uid)
        // eslint-disable-next-line unicorn/prefer-ternary
        if (index >= 0) {
          yield put(EventDuck.action[EVENT_ACTIONS.ITEM_REPLACE]({ from: data?.uid, to: editor.item.uid }))
        } else {
          yield put(EventDuck.action[EVENT_ACTIONS.ITEM_SELECT]({ from: data.uid, to: editor.item.uid }))
        }
        yield put(duckFace.action[EDITOR_ACTIONS.RESET]())
      } else {
        // Open replace editor for item
        yield put(duckFace.action[EDITOR_ACTIONS.SET_REPLACE](action.payload))
      }
      // yield put(duckFace.action[EDITOR_ACTIONS.CHANGE](action.payload))
    } else if ('add' === editorType && action.payload && Object.keys(action.payload).length) {
      yield put(duckFace.action[EDITOR_ACTIONS.CHANGE](action.payload))
    } else {
      yield put(duckFace.action[EDITOR_ACTIONS.RESET]())
    }
  })
})

EditorDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[EDITOR_ACTIONS.IMAGE_UPLOAD].actionType], function* runEvery(action) {
    const { file } = action.payload || {}
    const [initialError, response] = yield call(safelyExecuteSaga, imageUploadSaga, file)
    const { error } = response || {}
    yield initialError || error
      ? put(duckFace.action[EDITOR_ACTIONS.IMAGE_UPLOAD_FAIL]({ error: initialError || error }))
      : put(duckFace.action[EDITOR_ACTIONS.IMAGE_UPLOAD_SUCCESS]({ image: response }))
  })
})

// module export

export default EditorDuck
